/**
 * MintPage.jsx — rebuilt.
 *
 * Layout:
 *   [ media + description + socials ]  |  [ supply, quantity, mint ]
 *   phase cards (price / cap / eligibility per phase)
 *   my hexabots  →  each links to its OpenSea page
 *   roadmap
 *   community
 *
 * Deliberate omissions:
 *   - No gas figures anywhere. The wallet estimates gas at signing time
 *     using live network conditions; anything shown here would be a
 *     stale guess sitting next to the wallet's real number, and the two
 *     disagreeing just makes people hesitate.
 *   - No transaction sent on the wrong network. `getSigner()` re-reads
 *     the chain ID from the wallet immediately before signing, so a
 *     mid-session network switch is caught rather than broadcast to
 *     whatever chain happens to be selected.
 */
import React, { useMemo, useState } from 'react';
import { ethers } from 'ethers';
import Panel from '../Panel';
import GlowText from '../GlowText';
import RoboticButton from '../RoboticButton';
import SupplyProgress from './SupplyProgress';
import PhaseCards from './PhaseCards';
import WalletPicker from './WalletPicker';
import TeamMintPanel from './TeamMintPanel';
import MintLocked from './MintLocked';
import MyNFTs from './MyNFTs';
import Roadmap from './Roadmap';
import useWallet from '../../hooks/useWallet';
import useMintContract, { PHASE } from '../../hooks/useMintContract';
import useMintConfig from '../../hooks/useMintConfig';
import { NATIVE_DECIMALS, NATIVE_SYMBOL, ARC_TESTNET } from '../../lib/chain';
import { CONTRACT_ADDRESS, PRODUCTION_CONTRACT_ADDRESS } from '../../lib/contractConfig';

const PREVIEW_MEDIA = 'https://res.cloudinary.com/pz3yhkrd/video/upload/v1783148711/video_2026-07-04_12-34-47_gnzsne.mp4';
/** Units the team keeps back. Not enforced on-chain — see TeamMintPanel. */
const TEAM_RESERVE = 222;
const X_URL = 'https://x.com/HexaBots_';
const TG_URL = 'https://t.me/HEXABOTSOFFICIALS';

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');

const XIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21l-6.52 7.45L22.5 22h-6.83l-4.77-6.24L5.2 22H2.44l6.98-7.97L1.5 2h6.98l4.32 5.72L18.244 2Zm-1.19 18h1.71L7.05 4H5.24l11.82 16Z" />
  </svg>
);
const TgIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor" aria-hidden="true">
    <path d="M21.94 4.6c.27-1.18-.94-2.13-2.02-1.68L2.4 10.13c-1.25.5-1.24 2.26.02 2.74l4.4 1.68 1.7 5.44c.2.63 1 .82 1.47.35l2.4-2.38 4.36 3.24c.98.72 2.38.19 2.65-1L21.94 4.6ZM8.6 13.6l9.5-6.72c.34-.24.7.2.4.48l-7.9 7.35c-.3.28-.5.66-.56 1.07l-.28 1.9-1.16-4.08Z" />
  </svg>
);

function SocialLink({ href, children, label }) {
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      aria-label={label}
      className="btn-robotic btn-robotic-cyan no-underline flex items-center justify-center"
      style={{ width: 44, height: 44, padding: 0 }}
    >
      {children}
    </a>
  );
}

export default function MintPage() {
  const wallet = useWallet();
  const mint = useMintContract(wallet.getSigner, wallet.address);
  const cfg = useMintConfig(mint.gtdStartTime, mint.publicStartTime);

  const [qty, setQty] = useState(1);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mintNonce, setMintNonce] = useState(0);   // bumps MyNFTs refresh

  const priceLabel = mint.deployed
    ? `${ethers.formatUnits(mint.mintPrice, NATIVE_DECIMALS)} ${NATIVE_SYMBOL}`
    : `— ${NATIVE_SYMBOL}`;

  const isOwner = Boolean(
    wallet.address && mint.owner &&
    wallet.address.toLowerCase() === mint.owner.toLowerCase()
  );

  // A cap of 0 means UNLIMITED, exactly as the contract reads it. Use
  // Infinity internally so the min() below simply falls through to the
  // per-transaction and remaining-supply limits.
  const capRemaining = useMemo(() => {
    if (mint.phase === PHASE.GTD) {
      return mint.maxPerWalletGTD === 0
        ? Infinity
        : Math.max(0, mint.maxPerWalletGTD - mint.userGtdMinted);
    }
    if (mint.phase === PHASE.PUBLIC) {
      return mint.maxPerWalletPublic === 0
        ? Infinity
        : Math.max(0, mint.maxPerWalletPublic - mint.userPublicMinted);
    }
    return 0;
  }, [mint.phase, mint.maxPerWalletGTD, mint.userGtdMinted, mint.maxPerWalletPublic, mint.userPublicMinted]);

  const supplyLeft = Math.max(0, mint.maxSupply - mint.totalSupply);
  const maxQty = Math.max(1, Math.min(
    capRemaining === Infinity ? Number.MAX_SAFE_INTEGER : (capRemaining || 1),
    mint.maxPerTx || 1,
    supplyLeft || 1
  ));
  const total = mint.deployed ? mint.mintPrice * BigInt(qty) : 0n;

  // ── Button state ──
  let label = 'MINT';
  let disabled = true;
  let note = null;

  if (!mint.deployed) {
    label = 'CONTRACT NOT DEPLOYED';
  } else if (wallet.availableWallets.length === 0) {
    label = 'NO WALLET FOUND';
    note = 'Install MetaMask, Rabby, OKX Wallet, Phantom or another EVM wallet.';
  } else if (!wallet.address) {
    label = 'CONNECT WALLET'; disabled = false;
  } else if (!wallet.isCorrectNetwork) {
    label = `SWITCH TO ${ARC_TESTNET.chainName.toUpperCase()}`; disabled = false;
    note = `Your wallet is on another network. Nothing will be sent until you switch to ${ARC_TESTNET.chainName}.`;
  } else if (mint.paused) {
    label = 'MINTING PAUSED';
  } else if (mint.phase === PHASE.SOLD_OUT) {
    label = 'SOLD OUT';
  } else if (mint.phase === PHASE.NONE) {
    label = 'NOT OPEN YET';
    note = mint.gtdStartTime
      ? `GTD opens ${new Date(mint.gtdStartTime * 1000).toUTCString().replace('GMT', 'UTC')}.`
      : 'Mint times have not been scheduled yet.';
  } else if (mint.phase === PHASE.GTD) {
    if (mint.whitelistStatus.error) {
      label = 'WHITELIST CHECK FAILED';
      note = "Couldn't load the whitelist. Refresh and try again — this doesn't mean you're excluded.";
    } else if (!mint.whitelistStatus.checked) {
      label = 'CHECKING ELIGIBILITY…';
    } else if (!mint.whitelistStatus.isWhitelisted) {
      label = 'NOT WHITELISTED';
      note = 'This wallet isn\u2019t on the GTD list. Public mint opens to everyone shortly after.';
    } else if (capRemaining <= 0) {
      label = 'GTD LIMIT REACHED';
    } else {
      label = `MINT ${qty}`; disabled = false;
    }
  } else if (mint.phase === PHASE.PUBLIC) {
    if (capRemaining <= 0) label = 'WALLET LIMIT REACHED';
    else { label = `MINT ${qty}`; disabled = false; }
  }

  const onMint = async () => {
    setError(null); setSuccess(null);
    if (!wallet.address) { setPickerOpen(true); return; }
    if (!wallet.isCorrectNetwork) { await wallet.switchToArc(); return; }
    try {
      if (mint.phase === PHASE.GTD) await mint.mintGTD(qty, wallet.address);
      else if (mint.phase === PHASE.PUBLIC) await mint.mintPublic(qty, wallet.address);
      else return;
      setSuccess(`Minted ${qty} HEXABOT${qty > 1 ? 's' : ''}.`);
      setQty(1);
      setMintNonce((n) => n + 1);
    } catch (e) {
      setError(e?.message || 'Mint failed.');
    }
  };

  if (cfg.loading) {
    return (
      <Panel title="MINT TERMINAL">
        <div className="flex flex-col items-center gap-3 py-16">
          <span className="status-dot" />
          <GlowText as="div" text="INITIALISING" size={14} variant="cyan" />
        </div>
      </Panel>
    );
  }
  if (cfg.pageLocked) {
    return <MintLocked unlockAt={cfg.mintPageUnlockAt} announcement={cfg.announcement} />;
  }
  if (!cfg.mintPageEnabled) {
    return (
      <Panel title="MINT TERMINAL // OFFLINE">
        <div className="text-center py-12 space-y-3">
          <GlowText as="div" text="TEMPORARILY OFFLINE" size={17} variant="amber" />
          <p className="font-mono-hud text-dim text-fluid-md max-w-md mx-auto">
            {cfg.pausedMessage || 'The mint terminal is unavailable right now. Check back shortly.'}
          </p>
        </div>
      </Panel>
    );
  }

  return (
    <div className="space-y-6">

      {/* ══ Header ══ */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <GlowText as="div" text="MINT TERMINAL" size={24} variant="cyan" />
          <span className="font-mono-hud text-dim text-xs tracking-widest">
            {ARC_TESTNET.chainName.toUpperCase()}{' // 2,222 UNITS'}
          </span>
        </div>

        {wallet.address ? (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="hud-chip">
              <span
                className="status-dot"
                style={{
                  background: wallet.isCorrectNetwork ? 'var(--cyan)' : 'var(--danger)',
                  boxShadow: `0 0 8px ${wallet.isCorrectNetwork ? 'var(--cyan)' : 'var(--danger)'}`,
                }}
              />
              <span className="font-mono-hud text-xs" style={{ color: 'var(--text)' }}>
                {wallet.walletName ? `${wallet.walletName} · ` : ''}{shortAddr(wallet.address)}
              </span>
            </div>
            {!wallet.isCorrectNetwork && (
              <RoboticButton size="sm" variant="danger" sheen={false} onClick={wallet.switchToArc}>
                SWITCH NETWORK
              </RoboticButton>
            )}
            <RoboticButton size="sm" variant="ghost" sheen={false} onClick={wallet.disconnect}>
              DISCONNECT
            </RoboticButton>
          </div>
        ) : (
          <RoboticButton variant="cyan" onClick={() => setPickerOpen(true)} disabled={wallet.connecting}>
            {wallet.connecting ? 'CONNECTING…' : 'CONNECT WALLET'}
          </RoboticButton>
        )}
      </div>

      {wallet.error && (
        <div className="mech-panel" style={{ padding: '10px 16px', borderColor: 'var(--danger)' }}>
          <span className="font-mono-hud text-fluid-sm glow-danger">{wallet.error}</span>
        </div>
      )}
      {CONTRACT_ADDRESS !== PRODUCTION_CONTRACT_ADDRESS && (
        <div className="mech-panel" style={{ padding: '10px 16px', borderColor: 'var(--violet)' }}>
          <span className="font-mono-hud text-fluid-sm glow-violet">
            TEST CONTRACT — {shortAddr(CONTRACT_ADDRESS)}
          </span>
        </div>
      )}
      {cfg.gateBypassed && (
        <div className="mech-panel" style={{ padding: '10px 16px', borderColor: 'var(--amber)' }}>
          <span className="font-mono-hud text-fluid-sm glow-amber">
            LOCAL PREVIEW — visitors still see the countdown.
          </span>
        </div>
      )}
      {mint.deployed && !mint.rpcHealthy && (
        <div className="mech-panel" style={{ padding: '10px 16px', borderColor: 'var(--amber)' }}>
          <span className="font-mono-hud text-fluid-sm glow-amber">
            Network busy — supply may lag a few seconds. Minting still works.
          </span>
        </div>
      )}

      {/* ══ Media (left) + Mint & phases (right) ══ */}
      <div className="grid gap-5 lg:grid-cols-2" style={{ alignItems: 'start' }}>

          {/* ── LEFT: preview, description, socials ── */}
          <Panel title="UNIT PREVIEW // PRE-REVEAL" bodyClassName="space-y-4">
            <div
              className="w-full overflow-hidden"
              style={{ border: '1px solid var(--cyan)', boxShadow: '0 0 28px rgba(0,229,255,0.28)' }}
            >
              <video
                src={PREVIEW_MEDIA}
                autoPlay loop muted playsInline
                className="block w-full h-auto"
                style={{ background: '#05070c' }}
              />
            </div>

            <p className="font-mono-hud text-fluid-md" style={{ color: 'var(--text)', lineHeight: 1.6 }}>
              2,222 hex-headed robots broadcasting from a forgotten signal
              dimension. Every unit is a screen, and no two screens wear the
              same expression. Mint one, hold it, and it starts mining.
            </p>

            <div className="flex items-center gap-3">
              <SocialLink href={X_URL} label="HEXABOTS on X"><XIcon /></SocialLink>
              <SocialLink href={TG_URL} label="HEXABOTS on Telegram"><TgIcon /></SocialLink>
              <span className="font-mono-hud text-dim" style={{ fontSize: 11, marginLeft: 4 }}>
                {mint.revealed ? 'REVEALED' : 'ARTWORK REVEALS AFTER MINT-OUT'}
              </span>
            </div>
          </Panel>

        {/* ── RIGHT: one box — supply, phases, then quantity + mint inline ── */}
        <Panel title="MINT" bodyClassName="space-y-5">
          <SupplyProgress minted={mint.totalSupply} max={mint.maxSupply} />

          <PhaseCards
            phase={mint.phase}
            gtdStartTime={cfg.effectiveGtdStart}
            publicStartTime={cfg.effectivePublicStart}
            clockSkew={mint.clockSkew}
            onTick={mint.refresh}
            priceLabel={priceLabel}
            gtdCap={mint.maxPerWalletGTD}
            publicCap={mint.maxPerWalletPublic}
            connected={Boolean(wallet.address && wallet.isCorrectNetwork)}
            isOwner={isOwner}
            whitelistStatus={mint.whitelistStatus}
            gtdUsed={mint.userGtdMinted}
            publicUsed={mint.userPublicMinted}
            teamMinted={mint.teamMinted}
            teamReserve={TEAM_RESERVE}
          />

          <div style={{ borderTop: '1px solid var(--steel)', paddingTop: 18 }} className="space-y-4">
            <div className="hud-chip w-full justify-between">
              <span className="font-mono-hud text-dim text-xs">TOTAL</span>
              <GlowText
                text={mint.deployed ? `${ethers.formatUnits(total, NATIVE_DECIMALS)} ${NATIVE_SYMBOL}` : '—'}
                size={17} variant="violet" mono
              />
            </div>

            {/* Quantity and the mint button share one row. */}
            <div className="flex items-stretch gap-2.5">
              <div className="flex items-stretch shrink-0" style={{ gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="btn-robotic btn-robotic-ghost !px-0 !py-0"
                  style={{ width: 42, fontSize: 18 }}
                  disabled={qty <= 1}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <div
                  className="hud-chip justify-center"
                  style={{ width: 54, padding: 0 }}
                >
                  <GlowText text={String(qty)} size={18} variant="cyan" mono />
                </div>
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                  className="btn-robotic btn-robotic-ghost !px-0 !py-0"
                  style={{ width: 42, fontSize: 18 }}
                  disabled={qty >= maxQty}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              <RoboticButton
                variant={disabled ? 'ghost' : 'cyan'}
                fullWidth
                disabled={disabled || mint.txState.pending}
                onClick={onMint}
                className={!disabled ? 'cta-button' : ''}
                style={{ minHeight: 48 }}
              >
                {mint.txState.pending ? 'CONFIRMING…' : label}
              </RoboticButton>
            </div>

            {note && <p className="font-mono-hud text-dim text-fluid-sm text-center">{note}</p>}
            {error && <p className="font-mono-hud text-fluid-sm text-center glow-danger">{error}</p>}
            {success && <p className="font-mono-hud text-fluid-sm text-center glow-cyan">{success}</p>}
            {mint.txState.lastTxHash && (
              <p className="font-mono-hud text-dim text-fluid-sm text-center">
                <a
                  href={`${ARC_TESTNET.blockExplorerUrls[0]}/tx/${mint.txState.lastTxHash}`}
                  target="_blank" rel="noopener noreferrer" className="glow-cyan"
                >
                  View transaction ↗
                </a>
              </p>
            )}
          </div>
        </Panel>
      </div>

      {/* ══ Team reserve (owner only) ══ */}
      {isOwner && (
        <TeamMintPanel
          teamMinted={mint.teamMinted}
          totalSupply={mint.totalSupply}
          maxSupply={mint.maxSupply}
          pending={mint.txState.pending}
          onMint={async (q) => { await mint.mintTeam(wallet.address, q); setMintNonce((n) => n + 1); }}
        />
      )}

      {/* ══ Owned ══ */}
      {wallet.address && wallet.isCorrectNetwork && (
        <MyNFTs address={wallet.address} refreshKey={mintNonce} />
      )}

      {/* ══ Roadmap ══ */}
      <Roadmap />

      {/* ══ Community ══ */}
      <Panel title="COMMUNITY">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono-hud text-fluid-md" style={{ color: 'var(--text)', margin: 0 }}>
            Holder-gated Discord, giveaways and $BOTS news land in the
            official channels first.
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <SocialLink href={X_URL} label="HEXABOTS on X"><XIcon /></SocialLink>
            <SocialLink href={TG_URL} label="HEXABOTS on Telegram"><TgIcon /></SocialLink>
          </div>
        </div>
      </Panel>

      {pickerOpen && (
        <WalletPicker
          wallets={wallet.availableWallets}
          onSelect={async (uid) => { setPickerOpen(false); await wallet.connect(uid); }}
          onClose={() => setPickerOpen(false)}
          busy={wallet.connecting}
        />
      )}
    </div>
  );
}
